import React, { useState } from "react";
import { Button, Confirm, Icon } from "semantic-ui-react";
import { NumField } from "./NumField";

// Tabla editable de una lista de la instalación (formato v2).
// columns: [{ key, label, type: 'text'|'num'|'select'|'area'|'check'|'view', campo?, options?: [[valor, texto]], render?(fila, i) }]
//   'num' valida con las reglas de la hoja 30 (campo = nombre del dato; por defecto, key);
//   'view' muestra un valor calculado (render).
// Borrar una fila pide confirmación (hoja 32). onErrors(n) informa cuántos campos tienen un valor inválido.
export function EditableTable({ rows, columns, onChange, newRow, addLabel, emptyText, onErrors, t = (k) => k, rowClass }) {
  const [aBorrar, setABorrar] = useState(null);
  const [errores, setErrores] = useState({});

  const actualizar = (i, key, value) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  const marcarError = (celda, err) => {
    setErrores((prev) => {
      const next = { ...prev };
      if (err) next[celda] = err; else delete next[celda];
      onErrors?.(Object.keys(next).length);
      return next;
    });
  };
  const borrar = () => {
    const i = aBorrar;
    setABorrar(null);
    // los errores de las filas que siguen se corren una posición
    setErrores((prev) => {
      const next = {};
      Object.entries(prev).forEach(([c, e]) => {
        const [fi, k] = c.split(":");
        const n = Number(fi);
        if (n < i) next[c] = e; else if (n > i) next[`${n - 1}:${k}`] = e;
      });
      onErrors?.(Object.keys(next).length);
      return next;
    });
    onChange(rows.filter((_, idx) => idx !== i));
  };

  const celda = (col, r, i) => {
    const v = r[col.key];
    const etiqueta = `${col.label} (${t("energy_performance_row")} ${i + 1})`;
    switch (col.type) {
      case "view":
        return col.render(r, i);
      case "num":
        return <NumField value={v} campo={col.campo || col.key} ariaLabel={etiqueta} onChange={(x) => actualizar(i, col.key, x)} onError={(e) => marcarError(`${i}:${col.key}`, e)} />;
      case "select":
        return (
          <select aria-label={etiqueta} value={v == null ? "" : v} onChange={(e) => actualizar(i, col.key, e.target.value)}>
            {col.options.map(([val, txt]) => <option key={val} value={val}>{txt}</option>)}
          </select>
        );
      case "area":
        return <textarea aria-label={etiqueta} rows={2} value={v || ""} onChange={(e) => actualizar(i, col.key, e.target.value)} />;
      case "check":
        return <input type="checkbox" aria-label={etiqueta} checked={!!v} onChange={(e) => actualizar(i, col.key, e.target.checked)} />;
      default:
        return <input type="text" aria-label={etiqueta} value={v == null ? "" : v} onChange={(e) => actualizar(i, col.key, e.target.value)} />;
    }
  };

  return (
    <div className="ep-table">
      <div className="ep-table__scroll">
        <table>
          <thead>
            <tr>
              {columns.map((c) => <th key={c.key} style={c.width ? { width: c.width } : undefined}>{c.label}</th>)}
              <th aria-label={t("energy_performance_actions")} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="ep-table__empty">{emptyText}</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.id || i} className={rowClass ? rowClass(r, i) : undefined}>
                {columns.map((c) => <td key={c.key} className={`ep-table__cell ep-table__cell--${c.type || "text"}`}>{celda(c, r, i)}</td>)}
                <td className="ep-table__del">
                  <Button type="button" icon basic size="mini" aria-label={t("energy_performance_delete_row")} onClick={() => setABorrar(i)}>
                    <Icon name="trash alternate outline" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {newRow && (
        <Button type="button" basic size="small" className="ep-table__add" onClick={() => onChange([...rows, newRow()])}>
          <Icon name="plus" /> {addLabel}
        </Button>
      )}
      <Confirm
        open={aBorrar != null}
        content={t("energy_performance_confirm_delete_row")}
        cancelButton={t("cancel")}
        confirmButton={t("energy_performance_delete")}
        onCancel={() => setABorrar(null)}
        onConfirm={borrar}
      />
    </div>
  );
}
